/**
 * The web half of notifications inside the app — which, like the gallery, is
 * almost none of it.
 *
 * There is no service worker on `capacitor://localhost`, so every line of
 * `client/src/sw.ts` that handles `push` and `notificationclick` is unreachable
 * in the shell. iOS shows the notification itself from the APNs payload, and
 * what is left for JavaScript is the two ends: registering a device token so
 * the server has somewhere to send, and picking up what the notification did
 * once somebody has acted on it. Both live in `ios/App/App/PushPlugin.swift`
 * and this file is their mirror.
 *
 * See docs/MOBILE.md, «What dies on native, and what it costs».
 */
import { isNative } from '../api/origin.ts';
import { withTimeout, PLUGIN_TIMEOUT_MS } from './gallery.ts';

/**
 * The same three words the web uses, so `pushEnvironment()` has one vocabulary
 * rather than one per platform. Swift maps `UNAuthorizationStatus` onto them.
 */
export type NativePermission = 'default' | 'granted' | 'denied';

/** One thing a notification did, on its way to being acted on and acknowledged. */
export interface NativePushEvent {
  /** True if somebody tapped it; false if it arrived with the app on screen. */
  tapped: boolean;
  /** In-app path to open. Absent on a payload older than the server that sent it. */
  url?: string;
  /** The server's receipt for this delivery; see api.ackPush. */
  deliveryId?: string;
}

/** What ios/App/App/PushPlugin.swift exposes. Kept in step by hand. */
export interface NativePushPlugin {
  permission(): Promise<{ permission: NativePermission }>;
  /**
   * Permission and token in one call, because the screen has no use for either
   * alone. `error` is why there is no token when permission was granted anyway
   * — no network at launch, or a build with no push entitlement.
   */
  register(): Promise<{ permission: NativePermission; token?: string; error?: string }>;
  /** Taps and foreground receipts since the last ask, cleared as they are read. */
  drainEvents(): Promise<{ events: NativePushEvent[] }>;
  /**
   * A nudge that the queue above has something in it, for the case the queue
   * alone never served: a notification tapped while the app is already on
   * screen.
   *
   * The queue exists because a tap can arrive before there is any JavaScript to
   * tell (see PushNotifications.enqueue), and what drained it was coming back to
   * the foreground — which is exactly what does not happen when somebody pulls
   * the shade down over a chat they are typing in and taps a banner from another
   * one. The app was never away, so nothing said anything, and the tap sat in
   * the queue until the next time the app was backgrounded.
   *
   * Optional: a shell older than this bundle never emits it, and there the tap
   * still opens the conversation on the next return to the foreground.
   */
  /**
   * Returns its handle synchronously and not as a promise, unlike every
   * `addListener` in this client that goes through `registerPlugin`: this
   * object is the one the native half writes into the page (see
   * `nativePushPlugin` below), and what `JSExport` puts there is a plain
   * `{ remove }` from `Capacitor.addListener`.
   */
  addListener?(event: 'pushEvent', handler: () => void): { remove: () => void };
  openSettings(): Promise<void>;
  /**
   * Takes one category's notifications out of the shade.
   *
   * `setAutoCancel` on Android and a tap on iOS only ever remove the one
   * notification somebody touched, so a conversation read inside the app leaves
   * its banner behind for good — and on Android the launcher counts those
   * banners, which is the «1» on an icon with nothing unread behind it.
   */
  clear(options: { tag: string }): Promise<void>;
  /**
   * Which conversation is on screen, or `null` for the list and everything else.
   *
   * The server already refuses to push a message to somebody reading that same
   * thread, so what reaches a phone in the foreground belongs to a *different*
   * conversation and has to be shown. Only this tells the native half which one
   * is which — without it the choice is «show all of them» or «show none», and
   * it used to be the second.
   */
  setActiveConversation(options: { conversationId: string | null }): Promise<void>;
  /**
   * Where the server is and who we are, for the half of the app that has no
   * page to ask.
   *
   * A notification's buttons are handled with no WebView running at all — a
   * broadcast receiver on Android — so the origin and the bearer have to be in
   * native storage before one is pressed. The same arrangement the gallery uses,
   * and the same obligation: an empty token when notifications are switched off
   * or somebody signs out, because a credential outliving its session is the one
   * thing this must not do.
   *
   * Optional, because a shell older than this bundle has never heard of it.
   */
  configure?(options: { origin: string; token: string }): Promise<void>;
  /**
   * Answers and declines pressed on a notification, in `CallKitEvent`'s shape so
   * that one reducer folds them wherever they came from. See
   * `drainNativePushCallActions` in lib/callKit.ts.
   */
  drainCallActions(): Promise<{ events: { action: string; callId: string; at: number }[] }>;
  /**
   * Whether an incoming call may take over the screen — Android 14 stopped
   * granting USE_FULL_SCREEN_INTENT to anything the installer does not consider
   * a calling app. Optional: no other platform has the question, and a shell
   * older than this bundle has never heard of it.
   */
  /**
   * What the OS itself thinks about notifications for this app, right now.
   * Optional: a shell older than this bundle has never heard of it.
   */
  diagnostics?(): Promise<Record<string, string | number | boolean>>;
  fullScreenPermission?(): Promise<{
    granted: boolean;
    /**
     * Whether this phone has that grant to give at all — Android 14 and up.
     * Optional as well, and for a subtler reason than the method itself: a shell
     * that answers `granted` without it is one built before the field existed,
     * where «true» still covers both «выдано» and «здесь такого нет».
     */
    applicable?: boolean;
  }>;
  openFullScreenSettings?(): Promise<void>;
}

/**
 * The plugin, or null off the phone.
 *
 * Read straight out of `window.Capacitor.Plugins`, synchronously, for the
 * reason `galleryPlugin()` spells out at length: the dynamic
 * `import('@capacitor/core')` this would otherwise be **never settles** inside
 * the shell, and a promise that settles never is a button that stays grey with
 * nothing to say about why.
 */
export function nativePushPlugin(): NativePushPlugin | null {
  if (!isNative) return null;
  const injected = (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } })
    .Capacitor?.Plugins?.Push;
  return (injected as NativePushPlugin | undefined) ?? null;
}

/**
 * The token this device last told the server about.
 *
 * Kept because «Отключить на этом устройстве» needs something to unsubscribe,
 * and the plugin cannot supply it: iOS has no "forget my token" call, only
 * `unregisterForRemoteNotifications`, and a token that is still valid on
 * Apple's side but no longer wanted here has to be deleted by the server. So
 * the last one posted is remembered rather than asked for again.
 *
 * It is also how `isSubscribed()` answers without a round trip, which keeps the
 * rule the dialog already depends on: the button follows the subscription, not
 * the permission — the two come apart on iOS all the time.
 */
const TOKEN_KEY = 'chat:push-token';

export function storedPushToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function rememberPushToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage unavailable. Notifications still arrive — the server has the
    // token — and the only thing lost is this device's ability to say it is
    // subscribed without asking, which the next successful register repairs.
  }
}

/** Permission as the plugin sees it, or `default` when there is no plugin. */
export async function nativePermission(): Promise<NativePermission> {
  const plugin = nativePushPlugin();
  if (!plugin) return 'default';
  try {
    const { permission } = await withTimeout(
      plugin.permission(),
      PLUGIN_TIMEOUT_MS,
      'разрешение на уведомления',
    );
    return permission;
  } catch {
    // A native half older than this bundle has no such method, and the bridge
    // answers that by not answering at all. Undecided is the honest report:
    // the button stays «Включить», and pressing it says something useful.
    return 'default';
  }
}
